import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Search, User, Building2, Banknote, Calendar, BookOpen, MessageSquare, X, ArrowRight } from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loading, EmptyState } from "@/components/common/Loading";
import { AccountTagInline } from "@/components/tags/AccountTag";
import { trackResult } from "@/lib/analytics";
import { useAnalyticsImpressions } from "@/hooks/useAnalyticsImpressions";

const EMPTY = { people: [], businesses: [], capital: [], events: [], resources: [], chatter: [] };

const PROFILE_PATH = { personal: "p", business: "b", capital: "capital" };

export const SECTIONS = [
  { key: "people", label: "People", Icon: User, round: true, id: (x) => x.slug, to: (x) => `/p/${x.slug}`, src: (x) => x.headshot_url, title: (x) => x.display_name, sub: (x) => x.headline },
  { key: "businesses", label: "Businesses", Icon: Building2, id: (x) => x.slug, to: (x) => `/b/${x.slug}`, src: (x) => x.profile_image_url, title: (x) => x.display_name, sub: (x) => x.headline },
  { key: "capital", label: "Capital", Icon: Banknote, id: (x) => x.slug, to: (x) => `/capital/${x.slug}`, src: (x) => x.profile_image_url, title: (x) => x.display_name, sub: (x) => x.headline },
  { key: "events", label: "Events", Icon: Calendar, id: (x) => x.slug, to: (x) => `/event/${x.slug}`, src: (x) => x.cover_url, title: (x) => x.title, sub: (x) => x.summary },
  { key: "resources", label: "Resources", Icon: BookOpen, id: (x) => x.slug, to: (x) => `/resource/${x.slug}`, src: (x) => x.cover_url, title: (x) => x.title, sub: (x) => x.summary },
  { key: "chatter", label: "Chatter", Icon: MessageSquare, id: (x) => x.post_id, to: (x) => `/${PROFILE_PATH[x.profile_type] || "p"}/${x.slug}`, src: () => null, title: (x) => x.snippet || "Update", sub: (x) => [x.author_name, x.profile_name].filter(Boolean).join(" · ") },
];

const PER_CATEGORY_PREVIEW = 4;
const PER_CATEGORY_FULL = 50;

function Thumb({ src, round, Icon }) {
  return (
    <div className={`flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden bg-secondary text-muted-foreground ${round ? "rounded-full" : "rounded-lg"}`}>
      {src ? <img src={fileUrl(src)} alt="" className="h-full w-full object-cover" /> : <Icon className="h-5 w-5" />}
    </div>
  );
}

export function ItemCard({ to, src, round, Icon, title, subtitle, tags, testid, trackRef, onTrackClick }) {
  return (
    <Link to={to} data-testid={testid} ref={trackRef} onClick={onTrackClick}>
      <Card className="flex items-center gap-3 border-0 p-4 shadow-[0_1px_2px_rgba(16,24,40,0.04)] transition-shadow hover:shadow-[0_10px_28px_rgba(16,24,40,0.10)]">
        <Thumb src={src} round={round} Icon={Icon} />
        <div className="min-w-0">
          <div className="truncate font-[640] text-foreground">{title || "Untitled"}</div>
          {tags?.length > 0 && <AccountTagInline tags={tags} max={1} size="xs" className="mt-0.5" />}
          {subtitle && <div className="truncate text-sm text-muted-foreground">{subtitle}</div>}
        </div>
      </Card>
    </Link>
  );
}

export default function SearchResults() {
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const [input, setInput] = useState(q);
  const [results, setResults] = useState(null);
  const bindImpression = useAnalyticsImpressions("search_impression");

  useEffect(() => { setInput(q); }, [q]);

  useEffect(() => {
    if (!q.trim()) { setResults(EMPTY); return undefined; }
    let ok = true;
    setResults(null);
    // Request a few extra items per category so we can correctly show the
    // "View all (N+)" indicator when there's more than the preview limit.
    api.get("/search", { params: { q: q.trim(), limit: PER_CATEGORY_PREVIEW + 1, surface: "search_results" } })
      .then(({ data }) => { if (ok) setResults({ ...EMPTY, ...data }); })
      .catch(() => { if (ok) setResults(EMPTY); });
    return () => { ok = false; };
  }, [q]);

  const submit = (e) => {
    e.preventDefault();
    const t = input.trim();
    setParams(t ? { q: t } : {});
  };

  const total = results ? SECTIONS.reduce((n, s) => n + (results[s.key] || []).length, 0) : 0;

  return (
    <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6 lg:px-8" data-testid="search-page">
      <h1 className="text-3xl font-[720] tracking-[-0.02em] text-foreground">Search</h1>
      <p className="mt-1 text-muted-foreground">Find people, businesses, capital, events, resources and chatter.</p>

      <form onSubmit={submit} className="mt-6 flex items-center gap-2">
        <div className="relative flex-1">
          <Input
            autoFocus
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Search everything…"
            data-testid="search-page-input"
          />
          {input && (
            <button type="button" onClick={() => setInput("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" aria-label="Clear">
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <Button type="submit" data-testid="search-page-submit">Search</Button>
      </form>

      <div className="mt-8">
        {!q.trim() ? (
          <EmptyState icon={Search} title="Search Startup Otter" description="Type a name, company, topic or keyword to explore the directory." testId="search-idle" />
        ) : results === null ? (
          <Loading label="Searching" />
        ) : total === 0 ? (
          <EmptyState icon={Search} title={`No results for "${q}"`} description="Try a different keyword or check your spelling." testId="search-empty" />
        ) : (
          <div className="space-y-10" data-testid="search-results">
            <p className="text-sm text-muted-foreground" data-testid="search-total">
              {total >= PER_CATEGORY_PREVIEW * SECTIONS.length ? `${total}+` : total} result{total === 1 ? "" : "s"} for &ldquo;{q}&rdquo;
            </p>
            {SECTIONS.map((s) => {
              const all = results[s.key] || [];
              if (all.length === 0) return null;
              const preview = all.slice(0, PER_CATEGORY_PREVIEW);
              const hasMore = all.length > PER_CATEGORY_PREVIEW;
              return (
                <section key={s.key} data-testid={`search-section-${s.key}`}>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <s.Icon className="h-5 w-5 text-primary" />
                      <h2 className="text-lg font-[680] text-foreground">{s.label}</h2>
                      <Badge variant="secondary">{hasMore ? `${PER_CATEGORY_PREVIEW}+` : all.length}</Badge>
                    </div>
                    {hasMore && (
                      <Link
                        to={`/search/${s.key}?q=${encodeURIComponent(q)}`}
                        className="inline-flex items-center gap-1 text-sm font-[560] text-primary hover:underline"
                        data-testid={`search-view-all-${s.key}`}
                      >
                        View all <ArrowRight className="h-3.5 w-3.5" />
                      </Link>
                    )}
                  </div>
                  <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                    {preview.map((x) => {
                      const itemId = s.id(x);
                      return (
                        <ItemCard
                          key={`${s.key}-${itemId}`}
                          to={s.to(x)}
                          src={s.src(x)}
                          round={s.round}
                          Icon={s.Icon}
                          title={s.title(x)}
                          subtitle={s.sub(x)}
                          tags={s.key === "people" ? x.account_tags : undefined}
                          testid={`search-result-${s.key}-${itemId}`}
                          trackRef={bindImpression(x.analytics)}
                          onTrackClick={x.analytics ? () => trackResult("search_result_clicked", x.analytics) : undefined}
                        />
                      );
                    })}
                  </div>
                </section>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// Reuse the constants on the category page for a single source of truth.
export { PER_CATEGORY_FULL };
